/**
 * Dependencies.
 */
import Moment from 'moment-timezone';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import DriverModel from '@/api/driver/driver.model';
import TrackingModel from '@/api/tracking/tracking.model';
import VehicleModel from '@/api/vehicle/vehicle.model';

/**
 * Component.
 */
@Component({ name: 'status-success-component' })
export default class extends Vue {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Property `windowHeight`;
   */
  windowHeight = null;

  /**
   * Property `client`.
   */
  @Prop({ type: String })
  client: string;

  /**
   * Property `driver`.
   */
  @Prop({ type: Object })
  driver: DriverModel;

  /**
   * Property `tracking`.
   */
  @Prop({ type: Object })
  tracking: TrackingModel;

  /**
   * Property `vehicle`.
   */
  @Prop({ type: Object })
  vehicle: VehicleModel;

  /**
   * Property `supportUrl`.
   */
  @Prop({ default: null })
  supportUrl: string;

  /**
   * Property `orderType`.
   */
  @Prop({ default: null, type: String })
  orderType: string;

  /**
   * Property `numberOfPackages`.
   */
  @Prop({ default: 0, type: Number })
  numberOfPackages: number;

  /**
   * Getter `isDelivery`
   */
  get isDelivery() {
    return this.orderType === 'DELIVERY';
  }

  /**
   * Getter success status title.
   */
  get successStatusTitle() {
    return this.i18n('TRACKING.SENTENCES.STATUS_HELLO', false, 'upperFirst', {
      name: this.client || '-'
    });
  }

  /**
   * Getter success status sentence.
   */
  get successStatusSentence() {
    const { statusDate } = this.tracking || {};
    const { name: driverName = null } = this.driver || {};

    const date = this.moment(statusDate).format('DD MMM YYYY');
    const hour = this.moment(statusDate).format('HH:mm');

    const support = `
      <a href="${
  this.supportUrl
}" class="status-success__support" target="_blank">
        ${this.i18n('TRACKING.MESSAGES.CALL_US')}</a>
    `;

    return this.i18n(
      `TRACKING.SENTENCES.STATUS_${
        this.isDelivery ? 'DELIVERY' : 'COLLECT'
      }_SUCCESS`,
      false,
      'plain',
      {
        date: `<b>${date || '-'}</b>`,
        driver: `<b>${driverName || '-'}</b>`,
        hour: `<b>${hour || '-'}</b>`,
        packages:
          this.numberOfPackages > 1
            ? this.i18n(
              'TRACKING.SENTENCES.NUMBER_OF_PACKAGES',
              false,
              'plain',
              { total: this.numberOfPackages }
            )
            : '',
        support: support.toLowerCase()
      }
    );
  }

  /**
   * Getter `extraSmallScreenHeight`.
   */
  get extraSmallScreenHeight() {
    return this.windowHeight <= 320;
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    this.handleOnResize();

    this.$nextTick(() => {
      window.addEventListener('resize', this.handleOnResize);
    });
  }

  /**
   * Lifecycle destroyed hook.
   */
  destroyed() {
    window.removeEventListener('resize', this.handleOnResize);
  }

  /**
   * Handle on resize.
   */
  handleOnResize() {
    this.windowHeight = window.innerHeight;
  }

  /**
   * Handle window height.
   */
  @Watch('windowHeight')
  handlewindowHeight(value) {
    this.windowHeight = value;
  }
}
